/**
 * Receipt image store backed by IndexedDB via idb-keyval. Receipts are
 * compressed to 800px-max JPEGs at 0.7 quality before persistence so
 * the store stays small.
 *
 * Every public function is wrapped in try/catch — IndexedDB failures
 * (quota, private mode, etc.) return null/0/[] instead of throwing.
 * Callers can treat receipts as best-effort and never need to gate UI
 * on success/failure.
 */

import {
  clear as idbClear,
  createStore,
  del as idbDel,
  get as idbGet,
  keys as idbKeys,
  set as idbSet,
  values as idbValues,
} from 'idb-keyval';
import { newId } from './id';

export interface StoredReceipt {
  id: string;
  txId: string;
  /** Compressed JPEG data URL. */
  thumbB64: string;
  createdAt: string;
  sizeBytes: number;
}

const STORE = createStore('margin-receipts', 'receipts');

const MAX_DIM = 800;
const QUALITY = 0.7;

/**
 * Compress a base64 data URL to a max-800px JPEG at 0.7 quality. Falls
 * back to the original input if canvas isn't available (e.g. SSR).
 */
async function compress(dataUrl: string): Promise<string> {
  if (typeof document === 'undefined' || typeof Image === 'undefined') {
    return dataUrl;
  }
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const ratio = Math.min(1, MAX_DIM / Math.max(img.width, img.height));
      const w = Math.round(img.width * ratio);
      const h = Math.round(img.height * ratio);
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        resolve(dataUrl);
        return;
      }
      ctx.drawImage(img, 0, 0, w, h);
      try {
        resolve(canvas.toDataURL('image/jpeg', QUALITY));
      } catch {
        resolve(dataUrl);
      }
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });
}

/** Best-effort byte estimate from a data URL. */
function estimateBytes(dataUrl: string): number {
  // Strip header `data:...;base64,` then base64 → bytes ≈ length × 0.75.
  const idx = dataUrl.indexOf(',');
  const body = idx >= 0 ? dataUrl.slice(idx + 1) : dataUrl;
  return Math.round(body.length * 0.75);
}

export async function saveReceipt(
  txId: string,
  fullImageB64: string,
): Promise<string | null> {
  try {
    const thumbB64 = await compress(fullImageB64);
    const id = newId();
    const receipt: StoredReceipt = {
      id,
      txId,
      thumbB64,
      createdAt: new Date().toISOString(),
      sizeBytes: estimateBytes(thumbB64),
    };
    await idbSet(id, receipt, STORE);
    return id;
  } catch {
    return null;
  }
}

export async function getReceipt(id: string): Promise<StoredReceipt | null> {
  try {
    const r = await idbGet<StoredReceipt>(id, STORE);
    return r ?? null;
  } catch {
    return null;
  }
}

export async function getReceiptByTxId(
  txId: string,
): Promise<StoredReceipt | null> {
  try {
    const all = (await idbValues<StoredReceipt>(STORE)) ?? [];
    return all.find((r) => r.txId === txId) ?? null;
  } catch {
    return null;
  }
}

export async function deleteReceipt(id: string): Promise<void> {
  try {
    await idbDel(id, STORE);
  } catch {
    /* swallow */
  }
}

export async function deleteReceiptByTxId(txId: string): Promise<void> {
  try {
    const all = (await idbValues<StoredReceipt>(STORE)) ?? [];
    const match = all.find((r) => r.txId === txId);
    if (match) await idbDel(match.id, STORE);
  } catch {
    /* swallow */
  }
}

export async function countReceipts(): Promise<number> {
  try {
    const ks = await idbKeys(STORE);
    return ks.length;
  } catch {
    return 0;
  }
}

export async function listAllReceiptIds(): Promise<string[]> {
  try {
    const ks = (await idbKeys(STORE)) as IDBValidKey[];
    return ks.map((k) => String(k));
  } catch {
    return [];
  }
}

export async function listAllReceipts(): Promise<StoredReceipt[]> {
  try {
    return ((await idbValues<StoredReceipt>(STORE)) ?? []).slice();
  } catch {
    return [];
  }
}

/**
 * Drop receipts whose txId isn't in the live transaction set. Returns
 * the count pruned. Called from the store on load to reclaim space
 * after deleted transactions.
 */
export async function pruneOrphanedReceipts(
  validTxIds: Set<string>,
): Promise<number> {
  try {
    const all = (await idbValues<StoredReceipt>(STORE)) ?? [];
    let pruned = 0;
    for (const r of all) {
      if (!validTxIds.has(r.txId)) {
        await idbDel(r.id, STORE);
        pruned += 1;
      }
    }
    return pruned;
  } catch {
    return 0;
  }
}

/**
 * Drop the N oldest receipts by createdAt. Returns the receipt IDs +
 * txIds that were removed so the caller can also clear receiptId on
 * those transactions.
 */
export async function pruneOldestReceipts(
  n: number,
): Promise<{ id: string; txId: string }[]> {
  if (n <= 0) return [];
  try {
    const all = (await idbValues<StoredReceipt>(STORE)) ?? [];
    const sorted = [...all].sort((a, b) =>
      a.createdAt.localeCompare(b.createdAt),
    );
    const targets = sorted.slice(0, n);
    for (const r of targets) {
      await idbDel(r.id, STORE);
    }
    return targets.map((r) => ({ id: r.id, txId: r.txId }));
  } catch {
    return [];
  }
}

export async function clearAllReceipts(): Promise<void> {
  try {
    await idbClear(STORE);
  } catch {
    /* swallow */
  }
}
